/**
 * Rappel à l'ouverture (maquette écran 3) : à la première ouverture du jour,
 * les jalons dont l'échéance est dépassée, du plus en retard au moins en retard.
 *
 * Bloquant par choix — c'est ce qui remplace le fait que « personne ne rouvre
 * le classeur ». On en sort par « Plus tard » ou en allant traiter un jalon.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Project } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiList } from '../../core/data/restClient';
import { useAuth } from '../../core/auth/useAuth';
import { colors } from '../../core/theme/theme';
import { milestoneCompliance, COMPLIANCE_COLOR } from './compliance';
import { useAllMilestones } from './useAllMilestones';

// ponytail: localStorage, pas de doc Firestore de préférence — le rappel est
// per-device et per-jour ; un aller-retour serveur n'ajouterait rien.
export const SEEN_KEY = 'milestoneReminderSeen';

/**
 * Jour **local**, volontairement différent des jours UTC de `compliance.ts` :
 * « une fois par jour » se compte dans la journée de l'utilisateur, sinon le
 * rappel se réarmerait en milieu de matinée à Shanghai.
 */
export function todayKey(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function MilestoneReminderDialog() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Lu une fois au montage : rien ne doit relancer un fetch après coup.
  const [seenToday] = useState(() => localStorage.getItem(SEEN_KEY) === todayKey());
  const enabled = Boolean(user) && !seenToday && !dismissed;

  const { rows } = useAllMilestones(enabled);
  const projects = usePolledResource<Project & { id: string }>(
    enabled ? 'reminder-projects' : null,
    () => apiList<Project & { id: string }>('/projects'),
  );
  const codeOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects.rows) m.set(p.id, p.code);
    return m;
  }, [projects.rows]);

  const now = useMemo(() => new Date(), []);
  const late = useMemo(
    () =>
      rows
        .map((r) => ({ row: r, c: milestoneCompliance(r, now) }))
        .filter((x) => x.c.daysLate > 0)
        .sort((a, b) => b.c.daysLate - a.c.daysLate),
    [rows, now],
  );

  const close = useCallback(() => {
    localStorage.setItem(SEEN_KEY, todayKey());
    setDismissed(true);
  }, []);

  const goTo = useCallback(
    (projectId: string, milestoneId: string) => {
      close();
      navigate(`/projects/${projectId}/milestones?milestone=${milestoneId}`);
    },
    [close, navigate],
  );

  if (!enabled || late.length === 0) return null;

  return (
    <Dialog
      open
      onClose={(_e, reason) => {
        // Un clic à côté ne compte pas comme « j'ai vu » : seuls les boutons du
        // pied (ou Échap, qui vaut « Plus tard ») ferment le rappel.
        if (reason !== 'backdropClick') close();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="milestone-reminder"
    >
      <DialogTitle>
        {t('projects.reminder.title', { count: late.length })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: colors.textMuted }}>
          {t('projects.reminder.help')}
        </Typography>
        {late.map(({ row, c }) => (
          <Box
            key={`${row.projectId}-${row.id}`}
            data-testid={`reminder-row-${row.projectId}-${row.id}`}
            onClick={() => goTo(row.projectId, row.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') goTo(row.projectId, row.id);
            }}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr auto', sm: '1fr 90px 80px 24px' },
              gap: 1.5,
              alignItems: 'center',
              px: 1.5,
              py: 1.25,
              cursor: 'pointer',
              borderLeft: `3px solid ${c.state === 'nok' ? colors.red : colors.yellow}`,
              borderBottom: `1px solid ${colors.lightGray}`,
              transition: 'background-color 150ms',
              '&:hover': { backgroundColor: colors.peach },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.name}
              </Typography>
              {row.reviewName && (
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {row.reviewName}
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{ display: { xs: 'none', sm: 'block' }, color: colors.textMuted }}
            >
              {codeOf.get(row.projectId) ?? row.projectId}
            </Typography>
            <Chip size="small" color={COMPLIANCE_COLOR[c.state]} label={`+${c.daysLate} j`} />
            <ChevronRightIcon
              sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 16, color: colors.textMuted }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} data-testid="reminder-later">
          {t('projects.reminder.later')}
        </Button>
        <Button
          variant="contained"
          data-testid="reminder-first"
          onClick={() => goTo(late[0].row.projectId, late[0].row.id)}
        >
          {t('projects.reminder.handleFirst')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
